import "./Form.css";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import { RuaAutoComplete } from "../RuaAutocomplete";
import {
    BAIRROS_API_URL,
    SOLICITACOES_API_URL,
} from "../../config/api";

import {
    attachmentErrorDetails,
    persistirAnexo,
    uploadExpiresAt,
} from "../../services/attachments";
import { RECAPTCHA_SITE_KEY } from "../../config/captcha";

const bairrosFallback = [
    { id: 1, nome: "Alegre" },
    { id: 2, nome: "Alto do Cruzeiro" },
    { id: 3, nome: "Barro Vermelho" },
    { id: 4, nome: "Centro" },
    { id: 5, nome: "Condom. Portal de Santa Cruz" },
    { id: 6, nome: "Conj. Cônego Monte" },
    { id: 7, nome: "Conj. Hab. Aluizio Bezerra" },
    { id: 8, nome: "Conj. Hab. Augusto Fernandes Pereira" },
    { id: 9, nome: "Conj. Hab. Manoel Borges da Silva" },
    { id: 10, nome: "Conj. Hab. Manoel Lourenço Lima" },
    { id: 11, nome: "DNER" },
    { id: 12, nome: "Gastão Nunes de Carvalho" },
    { id: 13, nome: "Jardim Pinheiros" },
    { id: 14, nome: "Lot. Alegre" },
    { id: 15, nome: "Lot. Alegre I" },
    { id: 16, nome: "Lot. Alegre II" },
    { id: 17, nome: "Lot. Bela Vista" },
    { id: 18, nome: "Lot. Mirante do Alto I" },
    { id: 19, nome: "Lot. Mirante do Alto II" },
    { id: 20, nome: "Lot. Nova Santa Cruz" },
    { id: 23, nome: "Lot. Santo Antônio" },
    { id: 21, nome: "Lot. São Lourenço V" },
    { id: 22, nome: "Lot. Vila Nova" },
    { id: 24, nome: "Mandala" },
    { id: 25, nome: "Maracujá" },
    { id: 26, nome: "Miguel Pereira Maia" },
    { id: 27, nome: "Paraíso" },
    { id: 28, nome: "Três a Um" },
    { id: 29, nome: "Vila Rica" },
    { id: 30, nome: "Zona Rural" },
];

const EMAIL_PATTERN = "[^\\s@]+@[^\\s@]+\\.[^\\s@]+";
const EMAIL_ERRO = "Digite um e-mail válido, por exemplo: nome@dominio.com.";

function emailValido(valor) {
    const emailNormalizado = valor.trim();
    if (!emailNormalizado || emailNormalizado.length > 254) return false;

    const partes = emailNormalizado.split("@");
    if (partes.length !== 2) return false;

    const [usuario, dominio] = partes;
    if (!usuario || usuario.length > 64 || usuario.startsWith(".") ||
        usuario.endsWith(".") || usuario.includes("..") || /\s/.test(usuario)) return false;

    const partesDominio = dominio.split(".");
    if (partesDominio.length < 2) return false;

    return partesDominio.every((parte) =>
        parte.length > 0 &&
        parte.length <= 63 &&
        /^[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/.test(parte)
    );
}

function mensagemErroEnvio(err) {
    const status = err?.response?.status;
    const resposta = err?.response?.data;
    const detalhe =
        typeof resposta === "string"
            ? resposta
            : resposta?.message || resposta?.erro || resposta?.error;

    if (status === 401 || status === 403) {
        return "Não foi possível validar o CAPTCHA. Recarregue a página, marque o CAPTCHA novamente e use o endereço oficial do site.";
    }

    if (!err?.response) {
        return "Não foi possível conectar ao servidor. Se estiver testando pelo celular em rede local, reinicie o servidor do front e acesse pelo endereço com proxy.";
    }

    return detalhe || "Revise os dados e tente enviar novamente.";
}

export const Form = ({ abrirModal, setAbrirModal, categoriaId, categoriaNome }) => {
    const [nome, setNome] = useState("");
    const [telefone, setTelefone] = useState("");
    const [email, setEmail] = useState("");
    const [endereco, setEndereco] = useState("");
    const [enderecoNumero, setEnderecoNumero] = useState("");
    const [bairro, setBairro] = useState("");
    const [bairros, setBairros] = useState([]);
    const [bairrosCarregando, setBairrosCarregando] = useState(false);
    const [bairrosErro, setBairrosErro] = useState("");
    const [comentario, setComentario] = useState("");
    const [captchaValido, setCaptchaValido] = useState("");
    const [captchaErro, setCaptchaErro] = useState(false);
    const [mensagem, setMensagem] = useState("");
    const [mostrarGif, setMostrarGif] = useState(false);
    const recaptchaRef = useRef(null);
    const envioRef = useRef(null);
    const enviandoRef = useRef(false);

    const [imagens, setImagens] = useState([]);
    const [imgErro, setImgErro] = useState("");
    const inputImgRef = useRef(null);

    const formatarTelefone = (valor) => {
        valor = valor.replace(/\D/g, "");
        valor = valor.slice(0, 11);

        if (valor.length <= 11) {
            return valor.replace(
                /(\d{0,2})(\d{0,4})(\d{0,4})/,
                (match, p1, p2, p3) =>
                    [p1 && `(${p1}`, p2 && `) ${p2}`, p3 && `${p3}`]
                        .filter(Boolean)
                        .join("")
            );
        }

        return valor.replace(
            /(\d{0,2})(\d{0,5})(\d{0,4})/,
            (match, p1, p2, p3) =>
                [p1 && `(${p1}`, p2 && ` ${p2}`, p3 && `${p3}`]
                    .filter(Boolean)
                    .join("")
        );
    };

    const handleCaptcha = (token) => {
        setCaptchaValido(token);
    };

    const MAX_MB = 5;
    const MAX_BYTES = MAX_MB * 1024 * 1024;
    const MAX_ORIGINAL_MB = 25;
    const MAX_ORIGINAL_BYTES = MAX_ORIGINAL_MB * 1024 * 1024;
    const MAX_IMAGENS = 3;
    const EXTENSOES_IMAGEM_ACEITAS = new Set([
        "jpg",
        "jpeg",
        "png",
        "webp",
        "gif",
        "bmp",
        "heic",
        "heif",
        "avif",
        "tif",
        "tiff",
    ]);

    function obterExtensaoArquivo(nome = "") {
        const match = String(nome).toLowerCase().match(/\.([a-z0-9]+)$/);
        return match?.[1] || "";
    }

    function pareceArquivoDeImagem(file) {
        const mime = String(file.type || "").toLowerCase();
        if (mime === "image/svg+xml") return false;
        if (mime.startsWith("image/")) return true;
        return EXTENSOES_IMAGEM_ACEITAS.has(obterExtensaoArquivo(file.name));
    }

    function ehHeicOuHeif(file) {
        const mime = String(file.type || "").toLowerCase();
        const extensao = obterExtensaoArquivo(file.name);
        return mime === "image/heic" || mime === "image/heif" || extensao === "heic" || extensao === "heif";
    }

    function validarImagem(file) {
        if (!pareceArquivoDeImagem(file)) {
            return "Formato nao suportado. Envie uma foto ou imagem comum.";
        }
        if (file.size > MAX_ORIGINAL_BYTES) {
            return `Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(
                1
            )}MB). Maximo ${MAX_ORIGINAL_MB}MB.`;
        }
        return "";
    }

    function carregarImagem(file) {
        return new Promise((resolve, reject) => {
            const objectUrl = URL.createObjectURL(file);
            const img = new Image();

            img.onload = () => {
                URL.revokeObjectURL(objectUrl);
                resolve(img);
            };

            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(
                    new Error(
                        "Este formato nao pode ser convertido neste aparelho. Tente JPG, PNG ou uma nova foto da camera."
                    )
                );
            };

            img.src = objectUrl;
        });
    }

    function canvasParaBlob(canvas, mimeType, quality) {
        return new Promise((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error("Nao foi possivel preparar a imagem para envio."));
                    }
                },
                mimeType,
                quality
            );
        });
    }

    async function converterHeicParaJpeg(file) {
        try {
            const modulo = await import("heic2any");
            const heic2any = modulo.default || modulo;
            const convertido = await heic2any({
                blob: file,
                toType: "image/jpeg",
                quality: 0.9,
            });
            const blob = Array.isArray(convertido) ? convertido[0] : convertido;

            if (!blob) {
                throw new Error("Conversao sem imagem gerada.");
            }

            const nomeBase = file.name?.replace(/\.\w+$/, "") || "foto";
            return new File([blob], nomeBase + ".jpg", {
                type: "image/jpeg",
                lastModified: Date.now(),
            });
        } catch (err) {
            console.error("Erro ao converter HEIC/HEIF:", err);
            throw new Error(
                "Nao foi possivel converter esta imagem HEIC/HEIF. Tente enviar uma foto em JPG ou PNG."
            );
        }
    }

    async function prepararImagemParaUpload(
        file,
        { maxW = 1280, maxH = 1280, quality = 0.85 } = {}
    ) {
        const arquivoBase = ehHeicOuHeif(file) ? await converterHeicParaJpeg(file) : file;
        const img = await carregarImagem(arquivoBase);
        let width = img.naturalWidth || img.width;
        let height = img.naturalHeight || img.height;

        if (!width || !height) {
            throw new Error("Nao foi possivel ler as dimensoes da imagem.");
        }

        const ratio = Math.min(maxW / width, maxH / height, 1);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        const qualidades = [quality, 0.75, 0.65, 0.55];
        let blob = null;

        for (const qualidade of qualidades) {
            blob = await canvasParaBlob(canvas, "image/jpeg", qualidade);
            if (blob.size <= MAX_BYTES) break;
        }

        if (!blob || blob.size > MAX_BYTES) {
            throw new Error(`Imagem muito grande apos a conversao. Maximo ${MAX_MB}MB.`);
        }

        const nomeBase = file.name?.replace(/\.\w+$/, "") || "foto";
        const fileOut = new File([blob], nomeBase + ".jpg", {
            type: "image/jpeg",
            lastModified: Date.now(),
        });

        return { fileOut, mimeOut: "image/jpeg" };
    }

    async function onSelecionarImagem(e) {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const vagasDisponiveis = MAX_IMAGENS - imagens.length;
        if (vagasDisponiveis <= 0) {
            setImgErro(`Voce pode enviar no maximo ${MAX_IMAGENS} imagens.`);
            if (inputImgRef.current) inputImgRef.current.value = "";
            return;
        }

        const arquivosParaProcessar = files.slice(0, vagasDisponiveis);
        const erros = [];
        const novasImagens = [];

        for (const file of arquivosParaProcessar) {
            const msg = validarImagem(file);
            if (msg) {
                erros.push(`${file.name || "Arquivo"}: ${msg}`);
                continue;
            }

            try {
                const { fileOut } = await prepararImagemParaUpload(file, {
                    maxW: 1280,
                    maxH: 1280,
                    quality: 0.85,
                });
                const preview = URL.createObjectURL(fileOut);

                novasImagens.push({
                    id: `${Date.now()}-${fileOut.name}-${fileOut.size}-${Math.random()}`,
                    file: fileOut,
                    preview,
                });
            } catch (err) {
                erros.push(
                    `${file.name || "Arquivo"}: ${
                        err?.message || "Nao foi possivel preparar a imagem para envio."
                    }`
                );
            }
        }

        if (files.length > vagasDisponiveis) {
            erros.push(`Limite de ${MAX_IMAGENS} imagens atingido. As demais foram ignoradas.`);
        }

        if (novasImagens.length) {
            setImagens((atuais) => [...atuais, ...novasImagens].slice(0, MAX_IMAGENS));
        }

        setImgErro(erros.join(" "));
        if (inputImgRef.current) inputImgRef.current.value = "";
    }

    function removerImagem(id) {
        setImagens((atuais) => {
            const imagemRemovida = atuais.find((imagem) => imagem.id === id);
            if (imagemRemovida?.preview) {
                URL.revokeObjectURL(imagemRemovida.preview);
            }
            return atuais.filter((imagem) => imagem.id !== id);
        });
        setImgErro("");
        if (inputImgRef.current) inputImgRef.current.value = "";
    }

    function limparImagens() {
        setImagens((atuais) => {
            atuais.forEach((imagem) => {
                if (imagem.preview) URL.revokeObjectURL(imagem.preview);
            });
            return [];
        });
        setImgErro("");
        if (inputImgRef.current) inputImgRef.current.value = "";
    }

    function limparFormulario() {
        setNome("");
        setTelefone("");
        setEmail("");
        setBairro("");
        setEndereco("");
        setEnderecoNumero("");
        setComentario("");
        setCaptchaErro(false);
        limparImagens();
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (enviandoRef.current) return;
        if (!envioRef.current) {
            if (!captchaValido) {
                setCaptchaErro(true);
                return;
            }
            if (!nome.trim() || !telefone.trim() || !email.trim() ||
                !endereco.trim() || !enderecoNumero.trim() || !bairro.trim() || !comentario.trim()) return;
            if (!emailValido(email)) return;
        }

        enviandoRef.current = true;
        setMensagem("");
        setAbrirModal(false);
        setMostrarGif(true);
        try {
            if (!envioRef.current) {
                const { data } = await axios.post(SOLICITACOES_API_URL, {
                    nome, telefone, email, bairro, endereco, enderecoNumero, comentario, categoriaId,
                    captcha: captchaValido,
                    hasAttachment: imagens.length > 0,
                }, {
                    headers: { "Content-Type": "application/json" },
                    withCredentials: false,
                });
                // Record creation before any upload can fail; never create again on retry.
                envioRef.current = {
                    requestId: data?.id,
                    uploadToken: data?.uploadToken,
                    expiresAt: uploadExpiresAt(data?.uploadTokenExpiresIn),
                    imagens: imagens.map((imagem) => ({ ...imagem, progress: {} })),
                };
            }
            const envio = envioRef.current;
            if (envio.imagens.length && envio.expiresAt && Date.now() >= envio.expiresAt) {
                envio.expired = true;
                throw new Error("Prazo para anexar fotos expirado.");
            }
            for (const imagem of envio.imagens) {
                await persistirAnexo({ ...envio, file: imagem.file, progress: imagem.progress });
            }
            envioRef.current = null;
            limparFormulario();
            setMensagem(
                <div className="mensagem-sucesso">
                    <p>Solicitação enviada com sucesso!</p>
                    <p>Você receberá o acompanhamento desta solicitação pelo e-mail informado.</p>
                    <button type="button" onClick={() => setMensagem("")}>Fechar</button>
                </div>
            );
        } catch (err) {
            const envio = envioRef.current;
            if (envio) {
                const falhaAnexo = attachmentErrorDetails(err);
                const indisponivel = envio.expired || !envio.uploadToken || !envio.requestId ||
                    [401, 403].includes(falhaAnexo.status) || !falhaAnexo.retryable;
                const explicacao = falhaAnexo.message || (indisponivel
                    ? "O prazo ou a autorização para anexar fotos não está disponível. Guarde o número da solicitação para atendimento."
                    : "Não foi possível anexar todas as fotos. Tente novamente.");
                setMensagem(
                    <div className="mensagem-erro" role="alert">
                        <p>Solicitação {envio.requestId || ""} criada, mas há fotos pendentes.</p>
                        <p>{explicacao}</p>
                        {!indisponivel && (
                            <button type="button" onClick={handleSubmit}>Tentar anexar fotos novamente</button>
                        )}
                        <button type="button" onClick={() => {
                            envioRef.current = null;
                            limparFormulario();
                            setMensagem("");
                        }}>Concluir sem as fotos pendentes</button>
                    </div>
                );
            } else {
                const explicacao = mensagemErroEnvio(err);
                console.error(err);
                setMensagem(
                    <div className="mensagem-erro" role="alert">
                        <p>Não foi possível enviar a solicitação.</p>
                        <p>{explicacao}</p>
                        <button type="button" onClick={() => {
                            setMensagem("");
                            setAbrirModal(true);
                        }}>Voltar ao formulário</button>
                    </div>
                );
            }
        } finally {
            recaptchaRef.current?.reset();
            setCaptchaValido("");
            setCaptchaErro(false);
            setMostrarGif(false);
            enviandoRef.current = false;
        }
    };


    const fecharModal = () => {
        setAbrirModal(false);
        setNome("");
        setTelefone("");
        setEmail("");
        setBairro("");
        setEndereco("");
        setEnderecoNumero("");
        setComentario("");
        setCaptchaErro(false);
        limparImagens();
    };

    useEffect(() => {
        document.body.style.overflow = abrirModal ? "hidden" : "auto";
    }, [abrirModal]);

    useEffect(() => {
        if (!abrirModal) return;

        let cancelado = false;

        const carregarBairros = async () => {
            setBairrosCarregando(true);
            setBairrosErro("");
            try {
                const { data } = await axios.get(BAIRROS_API_URL, {
                    headers: { Accept: "application/json" },
                    withCredentials: false,
                });
                if (cancelado) return;
                let resposta = data;
                if (typeof resposta === "string") {
                    try {
                        resposta = JSON.parse(resposta);
                    } catch {
                        resposta = [];
                    }
                }

                const lista =
                    (Array.isArray(resposta) && resposta) ||
                    (Array.isArray(resposta?.data) && resposta.data) ||
                    (Array.isArray(resposta?.bairros) && resposta.bairros) ||
                    (Array.isArray(resposta?.items) && resposta.items) ||
                    (Array.isArray(resposta?.results) && resposta.results) ||
                    [];
                setBairros(lista.length ? lista : bairrosFallback);
            } catch (err) {
                console.error(err);
                if (cancelado) return;
                setBairros(bairrosFallback);
                setBairrosErro("");
            } finally {
                if (!cancelado) setBairrosCarregando(false);
            }
        };

        carregarBairros();

        return () => {
            cancelado = true;
        };
    }, [abrirModal]);

    return (
        <>
            {/* Modal do formulário */}
            {abrirModal && (
                <div>
                    <div className="campo-formulario animacao">
                        <button className="botao-fechar-modal" onClick={fecharModal}>
                            &times;
                        </button>
                        <form className="formulario" onSubmit={handleSubmit}>
                            <h3>
                                FAÇA SUA SOLICITAÇÃO
                                <br />
                                PREENCHENDO O FORMULÁRIO:
                            </h3>

                            {categoriaNome && (
                                <p className="categoria-selecionada">
                                    Categoria: <strong>{categoriaNome}</strong>
                                </p>
                            )}

                            <input
                                placeholder="NOME"
                                required
                                maxLength={120}
                                value={nome}
                                onChange={(e) => setNome(e.target.value)}
                            />

                            <input
                                placeholder="Telefone (WHATSAPP)"
                                required
                                type="text"
                                maxLength={20}
                                value={telefone}
                                onChange={(e) =>
                                    setTelefone(formatarTelefone(e.target.value))
                                }
                            />

                            <input
                                placeholder="E-MAIL (DIGITE UM E-MAIL VÁLIDO)"
                                required
                                type="email"
                                pattern={EMAIL_PATTERN}
                                title={EMAIL_ERRO}
                                maxLength={254}
                                value={email}
                                onInvalid={(e) => e.currentTarget.setCustomValidity(EMAIL_ERRO)}
                                onInput={(e) => e.currentTarget.setCustomValidity("")}
                                onChange={(e) => setEmail(e.target.value)}
                            />

                            <select
                                value={bairro}
                                onChange={(e) => setBairro(e.target.value)}
                                className={`select ${bairro === "" ? "placeholder" : ""}`}
                                disabled={bairrosCarregando}
                            >
                                <option value="" disabled hidden>
                                    SELECIONE O BAIRRO
                                </option>
                                {!bairrosCarregando &&
                                    !bairrosErro &&
                                    bairros.map((b) => {
                                        const label = b?.nome ?? b;
                                        const value = b?.nome ?? b;
                                        const key = b?.id ?? value;
                                        return (
                                            <option key={key} value={value}>
                                                {label}
                                            </option>
                                        );
                                    })}
                            </select>
                            {bairrosCarregando && (
                                <div className="erro-captcha">Carregando bairros...</div>
                            )}
                            {!bairrosCarregando && bairrosErro && (
                                <div className="erro-captcha">{bairrosErro}</div>
                            )}
                            {!bairrosCarregando &&
                                !bairrosErro &&
                                bairros.length === 0 && (
                                    <div className="erro-captcha">
                                        Nenhum bairro encontrado.
                                    </div>
                                )}

                            <div className="endereco-linha">
                                <RuaAutoComplete
                                    endereco={endereco}
                                    setEndereco={setEndereco}
                                    bairro={bairro}
                                />

                                <input
                                    className="numero-endereco"
                                    placeholder="Nº CASA"
                                    required
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={20}
                                    value={enderecoNumero}
                                    onChange={(e) => setEnderecoNumero(e.target.value)}
                                    aria-label="Numero do endereco"
                                />
                            </div>

                            <textarea
                                placeholder="COMENTÁRIO"
                                maxLength={2000}
                                required
                                value={comentario}
                                onChange={(e) => setComentario(e.target.value)}
                            />

                            {/* Campo de imagem */}
                            <div className="ssc-imgField">
                                <label htmlFor="imagem">Fotos (opcional, ate {MAX_IMAGENS})</label>

                                <label
                                    className="ssc-drop"
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={async (e) => {
                                        e.preventDefault();
                                        const files = e.dataTransfer.files;
                                        if (!files?.length) return;
                                        const fake = { target: { files } };
                                        await onSelecionarImagem(fake);
                                    }}
                                >
                                    <span className="ssc-dropText">
                                        Arraste ate {MAX_IMAGENS} imagens aqui ou{" "}
                                        <strong>toque para escolher</strong>
                                    </span>

                                    <input
                                        ref={inputImgRef}
                                        id="imagem"
                                        type="file"
                                        accept="image/*,.heic,.heif,.avif"
                                        multiple
                                        onChange={onSelecionarImagem}
                                        className="ssc-inputFile"
                                        aria-describedby="ajuda-imagem"
                                    />
                                </label>

                                <span className="ssc-help" id="ajuda-imagem">
                                    {imagens.length} de {MAX_IMAGENS} imagens selecionadas. Ate {MAX_ORIGINAL_MB}MB por imagem; o app converte para JPG antes do envio.
                                </span>

                                {imgErro && <div className="ssc-error">{imgErro}</div>}

                                {imagens.length > 0 && (
                                    <div className="ssc-preview">
                                        {imagens.map((imagem, index) => (
                                        <div className="ssc-preview-item" key={imagem.id}>
                                            <img src={imagem.preview} alt={`Pre-visualizacao ${index + 1}`} />
                                            <button
                                                type="button"
                                                className="ssc-removeBtn"
                                                onClick={() => removerImagem(imagem.id)}
                                                aria-label={`Remover imagem ${index + 1}`}
                                            >
                                                x
                                            </button>
                                        </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="re-captcha">
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={RECAPTCHA_SITE_KEY}
                                    onChange={handleCaptcha}
                                />
                                {captchaErro && (
                                    <div className="erro-captcha">Resolva o captcha</div>
                                )}
                            </div>

                            <button type="submit" className="botao-submit">
                                ENVIAR SOLICITAÇÃO
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal do GIF de carregamento */}
            {mostrarGif && (
                <div className="modal-gif">
                    <div className="caixa-gif caixa-gif--loading" role="status" aria-live="polite">
                        <div className="loading-spinner" aria-hidden="true" />
                        <p className="mensagem-enviando-solicitacao">
                            Enviando solicitação...
                        </p>
                    </div>
                </div>
            )}

            {/* Mensagem de sucesso ou erro */}
            {mensagem && (
                <div className="modal-gif">
                    <div className="caixa-gif">{mensagem}</div>
                </div>
            )}
        </>
    );
};

